// The product mark, from the SupplierClear design canvas: a rounded square in
// the product green with a smaller rounded square inset.
//
// SVG rather than a PNG/SVG asset in each app's public/ so there is one copy to
// change, and because the inner square is a *cut-out* (fillRule evenodd) rather
// than a light-coloured square painted on top — it shows whatever is behind it,
// which is correct on the white sidebar, on the warm page ground and on a
// tenant's own tint alike. A painted inner square would be a visible light patch
// on any of those.
//
// The fill is a CONSTANT, deliberately, and not `currentColor` or a brand token.
// This mark is the PRODUCT's identity, not the tenant's: a tenant that sets
// primaryColor to purple must not thereby repaint the SupplierClear logo. The
// earlier `currentColor` version did exactly that on the login page, where the
// caller passed `text-brand`. Tenant colour still drives everything around the
// mark — buttons, links, active nav — via --brand-*; it stops at the mark.
// The same HEX the brand tokens default to, not the canvas's oklch(0.40 0.08 155)
// it was converted from. They are the same colour in sRGB, but on a wide-gamut
// display an oklch fill renders more saturated than the sRGB hex — and the mark
// sits directly beside buttons painted from the hex default, where any drift
// between them would be visible.
const PRODUCT_MARK_COLOR = '#1C5434';

/**
 * The product's own name — the vendor, not the tenant.
 *
 * One exported constant because it is rendered in six places across three apps
 * (both shells' chrome, the mobile More screen, the auth footer) and read as a
 * comparison in PoweredByFooter. A tenant's own name is NOT this: that comes
 * from `branding.companyName` and must keep overriding it everywhere.
 */
export const PRODUCT_NAME = 'SupplierClear';

/** Where every "Powered by SupplierClear" credit links to (via ProductLink). */
export const PRODUCT_URL = 'https://supplierclear.com/';

/**
 * Is this brand name the vendor's own, i.e. should a "Powered by SupplierClear"
 * credit be suppressed as a duplicate?
 *
 * ONE predicate, shared, because there were two: PoweredByFooter trimmed and
 * lowercased while AdminShell's header used exact `!==`. A tenant stored as
 * `supplierclear`, or with trailing whitespace, therefore had the credit
 * suppressed on the auth screens and duplicated in the admin header — and
 * AdminShell's comment claimed it applied "the same guard".
 */
export function isVendorBrandName(brandName: string): boolean {
  return brandName.trim().toLowerCase() === PRODUCT_NAME.toLowerCase();
}

// The client FALLBACK's companyName in lib/branding.tsx — the ONLY long-form
// platform name the chrome ever renders. It is never persisted: the API seeds
// (and has always seeded) the short form ('SupplierClear', and 'Locate2u' before
// the rename), so any other "... Safety & Compliance" in stored branding —
// including "Locate2u Safety & Compliance" — was typed by an admin and is the
// tenant's own configuration, shown verbatim. Anchored and exact on purpose: the
// shells used to strip `/ Safety.*$/i` from ANY name, which truncated a real
// tenant such as "Acme Safety Services" to "Acme".
const FALLBACK_PLATFORM_NAME_RE = /^SupplierClear Safety & Compliance$/i;

/**
 * The tenant name as the chrome displays it (sidebar lockup, app bars, sign-in
 * logo fallback, 404 header, the visitor pass).
 *
 * - blank / null / whitespace-only → PRODUCT_NAME
 * - the client fallback's "SupplierClear Safety & Compliance" (any case) →
 *   PRODUCT_NAME
 * - anything else → the name, trimmed, otherwise verbatim — including
 *   "Locate2u Safety & Compliance", which only an admin could have stored.
 */
export function displayBrandName(companyName: string | null | undefined): string {
  const name = typeof companyName === 'string' ? companyName.trim() : '';
  if (!name || FALLBACK_PLATFORM_NAME_RE.test(name)) return PRODUCT_NAME;
  return name;
}

export function BrandMark({
  size = 26,
  wordmark,
  className = '',
}: {
  size?: number;
  /** Renders the product name beside the mark. Omit for the mark alone. */
  wordmark?: string;
  /** Layout only — spacing and alignment. The mark's colour is not overridable. */
  className?: string;
}) {
  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill={PRODUCT_MARK_COLOR}
        d="M6 0h14a6 6 0 0 1 6 6v14a6 6 0 0 1-6 6H6a6 6 0 0 1-6-6V6a6 6 0 0 1 6-6Zm4.5 8.5h5a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2Z"
      />
    </svg>
  );

  if (wordmark === undefined) {
    return <span className={`inline-flex ${className}`}>{mark}</span>;
  }

  // `max-w-full` + the `min-w-0` on the wordmark below: a flex item defaults to
  // `min-width: auto`, i.e. it refuses to shrink below its longest word, so a
  // long unbroken TENANT name (the login card passes `branding.companyName`,
  // not just PRODUCT_NAME) pushed the lockup past the card and gave the whole
  // phone page a horizontal scrollbar — measured at 390px: content right edge
  // 402 vs card 374, documentElement.scrollWidth 402. The plain <h1> this
  // replaced did not do that. No effect on a short name such as PRODUCT_NAME,
  // which is why the admin sidebar is unchanged.
  return (
    <span className={`inline-flex max-w-full items-center gap-2.5 ${className}`}>
      {mark}
      {/* The wordmark is deliberately not `currentColor`: the mark carries the
          brand colour, the name reads as text. Colouring both makes the lockup
          shout on a light sidebar.
          Tokenised with a fallback, like the other shared components — `text-ink`
          is an admin-only Tailwind alias and would emit nothing in the portal or
          the ops console, leaving the wordmark to inherit whatever is around it. */}
      <span className="min-w-0 break-words text-[17px] font-semibold tracking-[-0.022em] text-[var(--ui-text-ink,#0f172a)]">
        {wordmark}
      </span>
    </span>
  );
}
